// chats-layer/stores/chats.query-store.ts
import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { type InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { ChatsService } from '../services/chats.service'
import { chatsSocketService } from '../services/chats.socket-service'
import { ChatSocketEvent, ConversationEventType, MessageStatus } from '../types/chats.enums'
import {
  addTypingMessage,
  clearMessagesCache,
  collectOptimisticMessages,
  getConversationsListPredicate,
  mergeOptimisticMessagesIntoPage,
  type MessagesPage,
  removeConversationFromListCaches,
  removeTypingMessages,
  setConversationInAllCaches,
  updateConversationCache,
  updateConversationInAllCaches,
  updateConversationListCaches,
  updateConversationWithMessage,
  updateMessagesCache,
} from '../utils/cache-update'
import {
  getConversationEventSenderId,
  normalizeConversation,
  normalizeConversationRecipient,
  normalizeConversationsResponse,
} from '../utils/conversation.transform'
import { chatsQueryKeys } from '../utils/query-keys.utils'

import type {
  IConversation,
  IConversationPaginationParams,
  IConversationsResponse,
  ICreateConversationInput,
  IMessage,
  ISuggestionPaginationParams,
} from '../types/conversation.types'
import type { ChatSocketMessage, ConversationEventPayload, MessageSentPayload, SendMessagePayload } from '../types/socket.types'

interface SendMessageMutationInput {
  locationId: string
  payload: SendMessagePayload
  optimisticMessage: IMessage
}

export const useChatsQueryStore = () => {
  const service = new ChatsService()
  const queryClient = useQueryClient()

  // ===== QUERY CREATION FUNCTIONS =====

  /**
   * Fetch conversations with infinite scroll pagination
   */
  const fetchInfiniteConversationsQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    params?: MaybeRefOrGetter<Partial<IConversationPaginationParams> | undefined>
  ) => {
    const query = useInfiniteQuery({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        const p = toValue(params)
        return chatsQueryKeys.conversationsList(locId, p?.unread)
      }),
      queryFn: async ({ pageParam }) => {
        const locId = toValue(locationId)
        const p = toValue(params)
        if (!locId) {
          throw new Error('Location ID is required')
        }
        const response = await service.getConversations(locId, {
          ...p,
          lastMessageAtCursor: pageParam as string | undefined,
        })
        return normalizeConversationsResponse(response)
      },
      getNextPageParam: lastPage => {
        return lastPage.hasMore ? lastPage.nextCursor : undefined
      },
      initialPageParam: undefined as string | undefined,
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId)),
    })

    const allData = computed(() => query.data.value?.pages.flatMap(page => page.conversations) || [])

    return {
      ...query,
      allData,
    }
  }

  /**
   * Fetch a single conversation by ID
   */
  const fetchConversationQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    conversationId: MaybeRefOrGetter<string | undefined>
  ) => {
    return useQuery({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        const convId = toValue(conversationId)
        return chatsQueryKeys.conversation(locId, convId)
      }),
      queryFn: async () => {
        const locId = toValue(locationId)
        const convId = toValue(conversationId)
        if (!locId || !convId) {
          throw new Error('Location ID and Conversation ID are required')
        }
        const conversation = await service.getConversationById(locId, convId)
        return normalizeConversation(conversation)
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId) && !!toValue(conversationId)),
    })
  }

  /**
   * Imperatively resolve a single conversation by id (cache-first).
   *
   * NOT redundant with `fetchConversationQuery` — the difference is shape and
   * lifecycle, not "needs Vue context". Both obtain the query client via
   * `useQueryClient()` (inject), so constructing this store still needs a valid
   * inject context. The distinction is what you get back and how it lives:
   *
   *   - `fetchConversationQuery` is a `useQuery` composable: it registers an effect
   *     scope, watchers and `onScopeDispose` cleanup, keeps a live subscription, and
   *     returns reactive refs (`data`, `isLoading`, …). It must run inside component
   *     setup / an effect scope, and it never yields a one-shot value.
   *   - `ensureConversation` is a plain method on the already-constructed client
   *     (`queryClient.ensureQueryData`): it returns a `Promise<IConversation>`, has no
   *     effect scope or subscription, and can be awaited from anywhere once the store
   *     exists (e.g. a service-worker `message` listener or boot-time deep link,
   *     reached via `nuxtApp.runWithContext` which builds the store once).
   *
   * Use this for "resolve the value and act now"; use `fetchConversationQuery` for
   * reactive rendering. Both share the `conversation(locationId, id)` query key, so
   * they hit/populate the same cache — no duplicate fetch.
   */
  const ensureConversation = (locationId: string, conversationId: string): Promise<IConversation> => {
    return queryClient.ensureQueryData({
      queryKey: chatsQueryKeys.conversation(locationId, conversationId),
      queryFn: async () => normalizeConversation(await service.getConversationById(locationId, conversationId)),
      staleTime: 5 * 60 * 1000, // 5 minutes
    })
  }

  /**
   * Fetch messages in a conversation with infinite scroll pagination
   */
  const fetchInfiniteMessagesQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    conversationId: MaybeRefOrGetter<string | undefined>
  ) => {
    const query = useInfiniteQuery({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        const convId = toValue(conversationId)
        return chatsQueryKeys.messages(locId, convId)
      }),
      queryFn: async ({ pageParam }) => {
        const locId = toValue(locationId)
        const convId = toValue(conversationId)
        if (!locId || !convId) {
          throw new Error('Location ID and Conversation ID are required')
        }
        const page = await service.getMessages(locId, convId, pageParam as string | undefined)

        // First page only: keep optimistic bubbles across cancel/refetch races.
        // Otherwise the conversation list can show the new preview while the open
        // window briefly (or permanently) loses the in-flight message.
        if (pageParam !== undefined) {
          return page
        }

        const messagesKey = chatsQueryKeys.messages(locId, convId)
        const cached = queryClient.getQueryData<InfiniteData<MessagesPage>>(messagesKey)
        return mergeOptimisticMessagesIntoPage(page, collectOptimisticMessages(cached))
      },
      getNextPageParam: lastPage => {
        return lastPage.hasMore ? lastPage.nextCursor : undefined
      },
      initialPageParam: undefined as string | undefined,
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId) && !!toValue(conversationId)),
    })

    const allData = computed(() => query.data.value?.pages.flatMap(page => page.messages) || [])

    return {
      ...query,
      allData,
    }
  }

  /**
   * Search for conversation suggestions (members to chat with) with infinite scroll
   */
  const searchInfiniteSuggestionsQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    searchQuery: MaybeRefOrGetter<string>,
    params?: MaybeRefOrGetter<Partial<ISuggestionPaginationParams> | undefined>
  ) => {
    const query = useInfiniteQuery({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        const query = toValue(searchQuery)
        return chatsQueryKeys.suggestions(locId, query)
      }),
      queryFn: async ({ pageParam }) => {
        const locId = toValue(locationId)
        const searchText = toValue(searchQuery)
        const p = toValue(params)
        if (!locId) {
          throw new Error('Location ID is required')
        }
        return service.getConversationSuggestions(locId, {
          searchText,
          limit: p?.limit || 10,
          token: pageParam as string | undefined,
        })
      },
      getNextPageParam: lastPage => {
        return lastPage.hasMore ? lastPage.nextToken : undefined
      },
      initialPageParam: undefined as string | undefined,
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId)),
    })

    const allData = computed(() => query.data.value?.pages.flatMap(page => page.suggestions) || [])

    return {
      ...query,
      allData,
    }
  }

  /**
   * Fetch unread conversation count
   */
  const fetchUnreadCountQuery = (locationId: MaybeRefOrGetter<string | undefined>, enabled?: MaybeRefOrGetter<boolean>) => {
    return useQuery({
      queryKey: computed(() => {
        const locId = toValue(locationId)
        return chatsQueryKeys.unreadCount(locId)
      }),
      queryFn: async () => {
        const locId = toValue(locationId)
        if (!locId) {
          throw new Error('Location ID is required')
        }

        const data = await service.getConversations(locId, { unread: true, limit: 10 })
        return data.conversations.length
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => {
        const isEnabled = enabled ? toValue(enabled) : true
        return !!toValue(locationId) && isEnabled
      }),
    })
  }

  /**
   * Find an existing cached direct conversation by normalized recipientId.
   * Used to avoid creating duplicate DMs when conversation data already exists in cache.
   */
  const findCachedDirectConversationByRecipientId = (locationId: string, recipientId: string): IConversation | undefined => {
    if (!locationId || !recipientId) {
      return undefined
    }

    const cachedConversationEntries = queryClient.getQueriesData<InfiniteData<IConversationsResponse>>({
      predicate: getConversationsListPredicate(locationId),
    })

    const cachedConversations = cachedConversationEntries.flatMap(([, data]) => data?.pages.flatMap(page => page.conversations) ?? [])
    const uniqueConversations = Array.from(new Map(cachedConversations.map(conversation => [conversation._id, conversation])).values())

    return uniqueConversations.find(conversation => {
      const isDirectConversation = !conversation.type || conversation.type === 'DM'
      return isDirectConversation && conversation.recepients?.some(recipient => recipient.recipientId === recipientId)
    })
  }

  // ===== MUTATIONS =====

  /**
   * Mutation for creating a new conversation
   */
  const createConversationMutation = useMutation({
    mutationFn: async ({ locationId, input, groupId }: { locationId: string; input: ICreateConversationInput; groupId?: string }) => {
      const apiConversation = await service.createConversation(locationId, input, groupId)
      const conversation = normalizeConversation(apiConversation)

      if (conversation.recepients?.length || !input.recipient) {
        return conversation
      }

      return {
        ...conversation,
        recepients: [normalizeConversationRecipient(input.recipient)],
      }
    },
    onSuccess: (newConversation, variables) => {
      if (!newConversation) {
        return
      }

      setConversationInAllCaches(queryClient, variables.locationId, newConversation)
    },
  })

  /**
   * Mutation for sending a message via sockets
   */

  const sendMessageMutation = useMutation({
    mutationFn: async ({ locationId, payload }: SendMessageMutationInput) => {
      chatsSocketService.sendMessage(locationId, payload)
    },
    onMutate: async ({ locationId, payload, optimisticMessage }: SendMessageMutationInput) => {
      const messagesKey = chatsQueryKeys.messages(locationId, payload.conversationId)

      await queryClient.cancelQueries({ queryKey: messagesKey })

      const previousMessages = queryClient.getQueryData<InfiniteData<MessagesPage>>(messagesKey)
      const previousConversation = queryClient.getQueryData<IConversation>(chatsQueryKeys.conversation(locationId, payload.conversationId))
      const previousConversations = queryClient.getQueriesData<InfiniteData<IConversationsResponse>>({
        predicate: getConversationsListPredicate(locationId),
      })

      // Optimistically update messages cache
      updateMessagesCache(queryClient, messagesKey, messages => [optimisticMessage, ...removeTypingMessages(messages)])

      // Optimistically update conversation caches with new message
      const optimisticMessageWithRead = {
        ...optimisticMessage,
        createdAt: optimisticMessage.createdAt,
      }
      updateConversationWithMessage(queryClient, locationId, payload.conversationId, optimisticMessageWithRead)

      // Update lastReadAt in conversation cache
      const conversationKey = chatsQueryKeys.conversation(locationId, payload.conversationId)
      updateConversationCache(queryClient, conversationKey, conversation => ({
        ...conversation,
        lastReadAt: new Date().toISOString(),
      }))

      return {
        previousMessages,
        previousConversation,
        previousConversations,
        messagesKey,
      }
    },
    onError: (_err, variables, context) => {
      if (context?.messagesKey) {
        queryClient.setQueryData(context.messagesKey, context.previousMessages)
      }

      if (context?.previousConversation) {
        queryClient.setQueryData(
          chatsQueryKeys.conversation(context.previousConversation.locationId, context.previousConversation._id),
          context.previousConversation
        )
      }

      context?.previousConversations?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
  })

  /**
   * Send conversation-level socket events (typing indicators)
   */
  const sendConversationEvent = (locationId: string, input: ConversationEventPayload) => {
    chatsSocketService.sendConversationEvent(locationId, input)
  }

  /**
   * Mutation for marking a conversation as read
   */
  const markAsReadMutation = useMutation({
    mutationFn: ({ locationId, conversationId }: { locationId: string; conversationId: string }) =>
      service.markAsRead(locationId, conversationId),
    onMutate: async ({ locationId, conversationId }) => {
      // Get all conversation list query keys (both unread filters)
      const listQueryKeyUnread = chatsQueryKeys.conversationsList(locationId, true)
      const listQueryKeyAll = chatsQueryKeys.conversationsList(locationId, undefined)

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: listQueryKeyUnread })
      await queryClient.cancelQueries({ queryKey: listQueryKeyAll })

      // Snapshot the previous values for rollback
      const previousUnread = queryClient.getQueryData(listQueryKeyUnread)
      const previousAll = queryClient.getQueryData(listQueryKeyAll)

      // Helper to update pages
      const updatePages = (old: InfiniteData<IConversationsResponse> | undefined) => {
        if (!old?.pages) return old

        const newPages = old.pages.map((page: IConversationsResponse) => ({
          ...page,
          conversations: page.conversations.map((conv: IConversation) =>
            conv._id === conversationId ? { ...conv, unreadCount: 0, lastReadAt: new Date().toISOString() } : conv
          ),
        }))

        return { ...old, pages: newPages }
      }

      // Optimistically update both caches
      queryClient.setQueryData(listQueryKeyUnread, updatePages)
      queryClient.setQueryData(listQueryKeyAll, updatePages)

      return {
        previousUnread,
        previousAll,
        listQueryKeyUnread,
        listQueryKeyAll,
      }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousUnread && context?.listQueryKeyUnread) {
        queryClient.setQueryData(context.listQueryKeyUnread, context.previousUnread)
      }
      if (context?.previousAll && context?.listQueryKeyAll) {
        queryClient.setQueryData(context.listQueryKeyAll, context.previousAll)
      }
    },
    onSuccess: (_data, variables) => {
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.unreadCount(variables.locationId),
      })
    },
  })

  /**
   * Mutation for blocking a conversation
   */
  const blockConversationMutation = useMutation({
    mutationFn: ({ locationId, conversationId }: { locationId: string; conversationId: string }) =>
      service.blockConversation(locationId, conversationId),
    onSuccess: (_data, variables) => {
      updateConversationInAllCaches(queryClient, variables.locationId, variables.conversationId, conversation => ({
        ...conversation,
        isBlocked: true,
        blockedByCurrentUser: true,
      }))
    },
  })

  /**
   * Mutation for unblocking a conversation
   */
  const unblockConversationMutation = useMutation({
    mutationFn: ({ locationId, conversationId }: { locationId: string; conversationId: string }) =>
      service.unblockConversation(locationId, conversationId),
    onSuccess: (_data, variables) => {
      updateConversationInAllCaches(queryClient, variables.locationId, variables.conversationId, conversation => ({
        ...conversation,
        isBlocked: false,
        blockedByCurrentUser: false,
      }))
    },
  })

  /**
   * Mutation for deleting conversation history
   */
  const deleteConversationMutation = useMutation({
    mutationFn: ({ locationId, conversationId }: { locationId: string; conversationId: string }) =>
      service.deleteConversation(locationId, conversationId),
    onSuccess: (_data, variables) => {
      const messagesKey = chatsQueryKeys.messages(variables.locationId, variables.conversationId)
      const conversationKey = chatsQueryKeys.conversation(variables.locationId, variables.conversationId)
      clearMessagesCache(queryClient, messagesKey)
      updateConversationCache(queryClient, conversationKey, conversation => ({
        ...conversation,
        messages: [],
        lastMessageAt: undefined,
        lastMessagePreview: undefined,
        lastMessageType: undefined,
        lastMessageRelativeTime: undefined,
        unreadCount: 0,
      }))
      removeConversationFromListCaches(queryClient, variables.locationId, variables.conversationId)
    },
  })

  // ===== SOCKET HANDLERS =====

  /**
   * Handle received message from socket (incoming message from another user)
   */
  const handleReceiveMessage = (locationId: string, payload: IMessage) => {
    if (!payload?.conversationId) {
      return
    }

    const messagesKey = chatsQueryKeys.messages(locationId, payload.conversationId)
    updateMessagesCache(queryClient, messagesKey, messages => {
      const cleaned = removeTypingMessages(messages)
      // Skip if message already exists (duplicate detection)
      if (payload.uniqueRefId && cleaned.some(msg => msg.uniqueRefId === payload.uniqueRefId)) {
        return cleaned
      }
      return [payload, ...cleaned]
    })

    // Update conversation caches with new message
    updateConversationWithMessage(queryClient, locationId, payload.conversationId, payload)

    // If conversation not found in lists, invalidate to fetch it
    const found = updateConversationListCaches(queryClient, locationId, payload.conversationId, conversation => ({
      ...conversation,
      messages: [payload, ...removeTypingMessages(conversation.messages || [])],
      lastMessageAt: payload.createdAt,
    }))

    if (!found) {
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.conversations(locationId),
      })
    }

    // Invalidate unread count
    queryClient.invalidateQueries({
      queryKey: chatsQueryKeys.unreadCount(locationId),
    })
  }

  /**
   * Handle message sent confirmation from socket (optimistic update confirmation)
   */
  const handleMessageSent = (locationId: string, payload: MessageSentPayload) => {
    if (!payload?.conversationId) {
      return
    }

    const messagesKey = chatsQueryKeys.messages(locationId, payload.conversationId)
    const optimisticRefId = payload.uniqueRefId || payload.refId

    updateMessagesCache(queryClient, messagesKey, messages => {
      const cleaned = removeTypingMessages(messages)
      const index = optimisticRefId
        ? cleaned.findIndex(message => message.uniqueRefId === optimisticRefId || message._id === optimisticRefId)
        : -1

      // Update existing optimistic message or add new one
      if (payload.status === MessageStatus.FAILED && index > -1) {
        const updated = [...cleaned]
        // Keep optimistic message content; FAILED events can be sparse and only contain ids/status.
        updated[index] = { ...updated[index], status: MessageStatus.FAILED, isOptimistic: false }
        return updated
      }

      if (index > -1) {
        const updated = [...cleaned]
        // Merge server payload into optimistic message and force terminal status.
        updated[index] = {
          ...updated[index],
          ...payload,
          uniqueRefId: payload.uniqueRefId || updated[index].uniqueRefId,
          status: MessageStatus.SENT,
          isOptimistic: false,
        }
        return updated
      }

      return [payload, ...cleaned]
    })

    // Update conversation caches
    updateConversationWithMessage(queryClient, locationId, payload.conversationId, payload)
  }

  /**
   * Handle conversation events (typing indicators, etc.)
   */
  const handleConversationEvents = (locationId: string, payload: ConversationEventPayload) => {
    if (!payload?.conversationId) {
      return
    }

    const senderId = getConversationEventSenderId(payload)
    if (!senderId) {
      return
    }

    const messagesKey = chatsQueryKeys.messages(locationId, payload.conversationId)

    if (payload.eventType === ConversationEventType.TYPING) {
      // Add typing indicator to messages
      updateMessagesCache(queryClient, messagesKey, messages => addTypingMessage(messages, payload.conversationId, senderId))

      // Update conversation lists (skip if blocked)
      updateConversationListCaches(queryClient, locationId, payload.conversationId, conversation => {
        if (conversation.blockedBy?.length) {
          return {
            ...conversation,
            messages: removeTypingMessages(conversation.messages || []),
          }
        }

        return {
          ...conversation,
          messages: addTypingMessage(removeTypingMessages(conversation.messages || []), payload.conversationId, senderId),
        }
      })
    } else {
      // Remove typing indicators
      updateMessagesCache(queryClient, messagesKey, removeTypingMessages)
      updateConversationListCaches(queryClient, locationId, payload.conversationId, conversation => ({
        ...conversation,
        messages: removeTypingMessages(conversation.messages || []),
      }))
    }
  }

  /**
   * Handle incoming socket messages and sync TanStack caches
   */
  const handleSocketMessage = (message: ChatSocketMessage, locationIdOverride?: string) => {
    const resolvedLocationId = locationIdOverride || message.locationId || (message.payload as { locationId?: string })?.locationId

    if (!resolvedLocationId) {
      return
    }

    switch (message.event) {
      case ChatSocketEvent.RECEIVE_MESSAGE:
        handleReceiveMessage(resolvedLocationId, message.payload as IMessage)
        break
      case ChatSocketEvent.MESSAGE_SENT:
        handleMessageSent(resolvedLocationId, message.payload as MessageSentPayload)
        break
      case ChatSocketEvent.CONVERSATION_EVENTS:
        handleConversationEvents(resolvedLocationId, message.payload as ConversationEventPayload)
        break
      default:
        console.warn('[Chats] Unhandled socket event:', message.event)
    }
  }

  /**
   * Refresh server state after socket reconnects
   */
  const handleSocketReconnect = (locationId: string, conversationIds: string[] = []) => {
    if (!locationId) {
      return
    }

    queryClient.invalidateQueries({
      queryKey: chatsQueryKeys.conversations(locationId),
    })
    queryClient.invalidateQueries({
      queryKey: chatsQueryKeys.unreadCount(locationId),
    })

    conversationIds.forEach(conversationId => {
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.messages(locationId, conversationId),
      })
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.conversation(locationId, conversationId),
      })
    })
  }

  /**
   * Insert an optimistic message into the caches immediately, WITHOUT emitting
   * over the socket. Used by the voice-note flow, which must show the bubble in
   * history while the audio uploads, then emit the socket message once the upload
   * resolves (see `emitMessage` + `updateOptimisticMessage`).
   */
  const insertOptimisticMessage = (locationId: string, conversationId: string, optimisticMessage: IMessage) => {
    const messagesKey = chatsQueryKeys.messages(locationId, conversationId)
    updateMessagesCache(queryClient, messagesKey, messages => [optimisticMessage, ...removeTypingMessages(messages)])
    updateConversationWithMessage(queryClient, locationId, conversationId, optimisticMessage)
    updateConversationCache(queryClient, chatsQueryKeys.conversation(locationId, conversationId), conversation => ({
      ...conversation,
      lastReadAt: new Date().toISOString(),
    }))
  }

  /** Patch a cached (optimistic) message in place, matched by uniqueRefId / _id. */
  const updateOptimisticMessage = (locationId: string, conversationId: string, refId: string, updater: (message: IMessage) => IMessage) => {
    const messagesKey = chatsQueryKeys.messages(locationId, conversationId)
    updateMessagesCache(queryClient, messagesKey, messages =>
      messages.map(message => (message.uniqueRefId === refId || message._id === refId ? updater(message) : message))
    )
  }

  /** Emit an already-optimistic message over the socket (no cache insert). */
  const emitMessage = (locationId: string, payload: SendMessagePayload) => {
    chatsSocketService.sendMessage(locationId, payload)
  }

  return {
    // Query factory functions (fetch*/search* = data retrieval)
    fetchInfiniteConversationsQuery,
    fetchConversationQuery,
    ensureConversation,
    fetchInfiniteMessagesQuery,
    fetchUnreadCountQuery,
    searchInfiniteSuggestionsQuery,
    findCachedDirectConversationByRecipientId,

    // Mutations (create*/update*/delete* = data modification)
    createConversationMutation,
    sendMessageMutation,
    insertOptimisticMessage,
    updateOptimisticMessage,
    emitMessage,
    sendConversationEvent,
    markAsReadMutation,
    blockConversationMutation,
    unblockConversationMutation,
    deleteConversationMutation,

    // Socket handlers
    handleSocketMessage,
    handleSocketReconnect,

    // Query keys for manual invalidation
    queryKeys: chatsQueryKeys,
  }
}
